import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity as ActivityIcon,
  Loader2,
  AlertCircle,
  ChevronDown,
  Zap,
  Plug,
  LogIn,
  LogOut,
  ArrowUp,
  Clock3,
  Timer,
  PauseCircle,
  ShieldAlert,
  Sparkles,
  WifiOff,
  Wifi
} from 'lucide-react';
import { activityService } from '../services/activityService';
import { useAuthenticatedWebSocket } from '../hooks/useAuthenticatedWebSocket';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { ActivityEvent, ActivityFeed } from '../types';
import '../styles/activity.css';

const EVENT_ICONS: Record<string, typeof ActivityIcon> = {
  QueueJoined: LogIn,
  QueueLeft: LogOut,
  QueuePromoted: ArrowUp,
  QueuePostponed: Clock3,
  ChargingStarted: Zap,
  ChargingForceStarted: Zap,
  ChargingCompleted: Plug,
  ChargingForceStopped: PauseCircle,
  SessionExtended: Timer,
  SessionReduced: Timer,
  ChargerSetOnline: Wifi,
  ChargerSetOffline: WifiOff,
  AdminStoppedSession: ShieldAlert,
  DailyCleanup: Sparkles
};

/** Bucket for events that carry no chargerId (DailyCleanup and other system-wide events). */
const SYSTEM_GROUP_KEY = '__system__';

interface ChargerGroup {
  key: string;
  name: string;
  slotNumber?: number;
  events: ActivityEvent[];
}

export function ActivityPage() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  const [feed, setFeed] = useState<ActivityFeed | null>(null);
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadActivity = useCallback(async (isManualRefresh = false) => {
    try {
      if (isManualRefresh) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      const data = await activityService.getActivity({ limit: 200 });
      setFeed(data);
    } catch (err: any) {
      console.error('Failed to load activity feed:', err);
      setError(err?.response?.data?.message || err?.message || t('activity.loadError'));
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    loadActivity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live updates: append new events without a full reload. Dedupe by id since a concurrent
  // auto-refresh (below) can race with the SignalR push.
  const handleMessage = useCallback((message: any) => {
    if (message.type !== 'ActivityEvent') {
      return;
    }
    const incoming = message.data as ActivityEvent;
    setFeed(prev => {
      if (!prev) return prev;
      if (prev.events.some(e => e.id === incoming.id)) {
        return prev;
      }
      return { ...prev, events: [incoming, ...prev.events] };
    });
  }, []);

  const webSocketOptions = useMemo(() => ({ onMessage: handleMessage }), [handleMessage]);
  useAuthenticatedWebSocket(webSocketOptions);

  // Safety-net poll in case a SignalR event is missed (reconnect gap, dropped message).
  useAutoRefresh({ onRefresh: () => loadActivity(true) });

  const timeFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(i18n.language, {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: feed?.timeZoneId || undefined
      });
    } catch {
      return new Intl.DateTimeFormat(i18n.language, { hour: '2-digit', minute: '2-digit' });
    }
  }, [i18n.language, feed?.timeZoneId]);

  /* One section per charger, ordered by slot number so the page reads like the charger list.
     Events arrive newest-first and we push in order, so each group stays newest-first too. */
  const chargerGroups = useMemo<ChargerGroup[]>(() => {
    const groups = new Map<string, ChargerGroup>();

    for (const event of feed?.events || []) {
      const key = event.chargerId ?? SYSTEM_GROUP_KEY;
      let group = groups.get(key);

      if (!group) {
        group = {
          key,
          name: event.chargerName ?? t('activity.system'),
          slotNumber: event.slotNumber,
          events: []
        };
        groups.set(key, group);
      }

      // Not every event carries a slot number; keep the first one the charger reports.
      if (group.slotNumber === undefined && event.slotNumber !== undefined) {
        group.slotNumber = event.slotNumber;
      }

      group.events.push(event);
    }

    return Array.from(groups.values()).sort((a, b) => {
      if (a.key === SYSTEM_GROUP_KEY) return 1;
      if (b.key === SYSTEM_GROUP_KEY) return -1;
      if (a.slotNumber !== undefined && b.slotNumber !== undefined) {
        return a.slotNumber - b.slotNumber;
      }
      if (a.slotNumber !== undefined) return -1;
      if (b.slotNumber !== undefined) return 1;
      return a.name.localeCompare(b.name);
    });
  }, [feed, t]);

  const toggleGroup = useCallback((key: string) => {
    setCollapsedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const allCollapsed =
    chargerGroups.length > 0 && chargerGroups.every(group => collapsedKeys.has(group.key));

  const toggleAll = useCallback(() => {
    setCollapsedKeys(allCollapsed ? new Set() : new Set(chargerGroups.map(group => group.key)));
  }, [allCollapsed, chargerGroups]);

  const describeEvent = (event: ActivityEvent): string => {
    const key = `activity.event.${event.eventType}`;
    const fallback = event.actorName
      ? `${event.actorName} · ${event.eventType}`
      : `${t('activity.system')} · ${event.eventType}`;

    return t(key, {
      actor: event.actorName ?? t('activity.system'),
      target: event.targetName ?? '',
      charger: event.chargerName ?? '',
      slot: event.slotNumber ?? '',
      ...event.payload,
      defaultValue: fallback
    });
  };

  if (isLoading) {
    return (
      <div className="modern-loading">
        <Loader2 className="loading-spinner animate-spin" size={32} />
        <p className="loading-text">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modern-error">
        <div className="error-card">
          <div className="error-icon"><AlertCircle size={32} /></div>
          <h2 className="error-title">{t('activity.loadError')}</h2>
          <p className="error-message">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`modern-home-page activity-page ${isRTL ? 'rtl' : 'ltr'}`} dir={i18n.dir()}>
      <div className="modern-page-header">
        <div className="header-top">
          <div className="header-title-section">
            <h1 className="page-title">{t('activity.title')}</h1>
            <p className="page-subtitle">{feed?.date}</p>
          </div>
          {isRefreshing && <Loader2 className="animate-spin" size={20} />}
        </div>
      </div>

      {chargerGroups.length === 0 ? (
        <div className="modern-empty-state">
          <div className="empty-icon"><ActivityIcon size={40} /></div>
          <h3 className="empty-title">{t('activity.empty')}</h3>
        </div>
      ) : (
        <>
          <div className="activity-toolbar">
            <button type="button" className="activity-toggle-all" onClick={toggleAll}>
              {allCollapsed ? t('activity.expandAll') : t('activity.collapseAll')}
            </button>
          </div>

          <div className="activity-groups">
            {chargerGroups.map(group => {
              const isCollapsed = collapsedKeys.has(group.key);
              const panelId = `activity-panel-${group.key}`;

              return (
                <section className="activity-group" key={group.key}>
                  <button
                    type="button"
                    className="activity-group-header"
                    aria-expanded={isCollapsed ? 'false' : 'true'}
                    aria-controls={panelId}
                    onClick={() => toggleGroup(group.key)}
                  >
                    <ChevronDown className="activity-group-chevron" size={18} aria-hidden="true" />
                    <span className="activity-group-name">{group.name}</span>
                    <span className="activity-group-count">{group.events.length}</span>
                  </button>

                  {!isCollapsed && (
                    <ol className="activity-timeline" id={panelId}>
                      {group.events.map(event => {
                        const Icon = EVENT_ICONS[event.eventType] || ActivityIcon;
                        return (
                          <li key={event.id} className="activity-row">
                            <div className="activity-row-icon">
                              <Icon size={18} />
                            </div>
                            <div className="activity-row-body">
                              <p className="activity-row-text">{describeEvent(event)}</p>
                            </div>
                            <time className="activity-row-time" dateTime={event.occurredAt}>
                              {timeFormatter.format(new Date(event.occurredAt))}
                            </time>
                          </li>
                        );
                      })}
                    </ol>
                  )}
                </section>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
